import React, { useState, useEffect } from "react"

//controls
import {
  Button,
  Col,
  Form,
  Input,
  InputGroup,
  Label,
  Row,
  FormFeedback,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import Switch from "react-switch"

//react form
import { FormProvider, useForm, Controller } from "react-hook-form"
import Select from "react-select"
import { createSelector } from "reselect"

// Form Editor
import { Editor } from "react-draft-wysiwyg"
import { EditorState, ContentState } from "draft-js"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"

//navigation
import { Link, useNavigate, useParams } from "react-router-dom"

//validation
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"

//redux
import { useSelector, useDispatch } from "react-redux"

//custom hooks
import { useDeepCompareEffect } from "hooks"

//Date filter
import Moment from "moment"

import { isEmpty } from "lodash"

import {
  addNewNotification as onAddNewNotification,
  updateNotification as onUpdateNotification,
  resetNotification as onResetNotification,
  getNotificationEvents as onGetNotificationEvents,
  getNotificationRecipients as onGetNotificationRecipients,
} from "store/actions"
import { OnSymbol, Offsymbol } from "helpers/switch_helper"
import Spinners from "components/Common/Spinner"

const formdefault = {
  NotificationTitle: "",
  Hour: "",
  FollowUpReminderDays: "",
  MaxFollowUp: "",
  NotificationEvents: "",
  NotificationRecipients: "",
  SpecificRecipients: "",
  IsActive: true,
  Template: "",
}

function BasicInfoTab() {
  const navigate = useNavigate()
  const routeParams = useParams()
  const dispatch = useDispatch()
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  )

  /**
   * Form Validation Schema
   */
  const schema = yup.object().shape({
    NotificationTitle: yup
      .string()
      .required("Required")
      .min(3, "Title must be at least 3 characters"),
    FollowUpReminderDays: yup.string().nullable(),
    MaxFollowUp: yup.string().nullable(),
    NotificationEvents: yup
      .object()
      .required("Required")
      .transform((_, val) => (val ? val : null)),
    Hour: yup
      .number()
      .nullable() // Allow null when not required
      .typeError("Required")
      .when("NotificationEvents", {
        is: value => value?.EventTypeID === 2, // Scheduled events
        then: schema => schema.required("Required"),
        otherwise: schema => schema.nullable(), // Accept null when not EventTypeID 2
      }),
    NotificationRecipients: yup
      .object()
      .required("Required")
      .transform((_, val) => (val ? val : null)),
    SpecificRecipients: yup
      .string()
      .when("NotificationRecipients", {
        is: value => value?.RecipientID === 2, //Specific Recipients
        then: schema => schema.required("Required"),
        otherwise: schema => schema,
      })
      .nullable(),
    IsActive: yup.bool(),
    Template: yup
      .string()
      .required("Required")
      .transform((_, val) => (val ? val : null)),
  })

  const methods = useForm({
    mode: "onChange",
    defaultValues: formdefault,
    resolver: yupResolver(schema),
  })
  const { reset, control, formState, setValue, getValues, watch, trigger } =
    methods
  const { errors, isValid } = formState

  const NotificationRecipient = watch("NotificationRecipients")
  const NotificationEvent = watch("NotificationEvents")

  useDeepCompareEffect(() => {
    function updateNotificationState() {
      dispatch(onGetNotificationEvents())
      dispatch(onGetNotificationRecipients())
      //reset form with server data
      //reset(notification)
    }

    updateNotificationState()
  }, [dispatch])

  //////////////////STATE/////////////////////
  const { notification, loading, notificationEvents, notificationRecipients } =
    useSelector(state => state.notification)
  const [isLoading, setLoading] = useState(loading)
  ///////////////////////////////////////

  // useEffect(() => {
  //   return () => {
  //     dispatch(onResetNotification())
  //   }
  // }, [dispatch])

  const onCancelClick = () => {
    navigate("/notifications")
  }

  const handleSave = () => {
    trigger() //for form validation
    if (isValid) {
      const { id } = routeParams

      if (id === "new") {
        dispatch(onAddNewNotification(getValues()))
      } else {
        dispatch(onUpdateNotification(getValues()))
      }
      navigate("/notifications")
    }
  }

  useEffect(() => {
    const { id } = routeParams

    if (id === "new") {
      reset(formdefault)
    } else {
      if (!isEmpty(notification)) {
        const result = {
          ...notification,
          NotificationEvents: {
            EventID: notification.EventID,
            EventTitle: notification.EventTitle,
            EventTypeID: notification.EventTypeID,
          },
          NotificationRecipients: {
            RecipientID: notification.RecipientID,
            Recipient: notification.Recipient,
          },
        }

        const contentState = ContentState.createFromText(notification.Template)
        const editorState = EditorState.createWithContent(contentState)
        setEditorState(editorState)

        reset(result)
      }
    }
  }, [notification])

  const handleEditorChange = editorState => {
    setEditorState(editorState)
    const content = editorState.getCurrentContent().getPlainText()
    setValue("Template", content)
  }

  if (isLoading) {
    return <Spinners setLoading={setLoading} />
  }

  return (
    <FormProvider {...methods}>
      <Row>
        <Col sm="6">
          <div className="mb-3">
            <Label>Title</Label>
            <Controller
              name="NotificationTitle"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    id="NotificationTitle"
                    type="text"
                    required
                    invalid={!!errors.NotificationTitle}
                  />
                  {errors?.NotificationTitle?.message ? (
                    <FormFeedback type="invalid" className="d-block">
                      {errors?.NotificationTitle?.message}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            />
          </div>
          <div className="mb-3">
            <Label>Event</Label>
            <Controller
              name="NotificationEvents"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    id="NotificationEvents"
                    options={notificationEvents}
                    getOptionLabel={option => option.EventTitle}
                    getOptionValue={option => option.EventID}
                    required
                    aria-invalid={!!errors.NotificationEvents}
                    classNamePrefix="select2-selection"
                  />
                  {errors?.NotificationEvents?.message ? (
                    <FormFeedback type="invalid" className="d-block">
                      {errors?.NotificationEvents?.message}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            />
          </div>
          {NotificationEvent.EventTypeID === 2 ? (
            <div className="mb-3">
              <Label>Hour</Label>
              <Controller
                name="Hour"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      id="Hour"
                      type="number"
                      required
                      invalid={!!errors.Hour}
                    />
                    {errors?.Hour?.message ? (
                      <FormFeedback type="invalid" className="d-block">
                        {errors?.Hour?.message}
                      </FormFeedback>
                    ) : null}
                  </>
                )}
              />
            </div>
          ) : (
            ""
          )}
          <div className="mb-3">
            <Label>Follow Up Reminder</Label>
            <Controller
              name="FollowUpReminderDays"
              control={control}
              render={({ field }) => (
                <>
                  <InputGroup>
                    <Input
                      {...field}
                      id="FollowUpReminderDays"
                      type="text"
                      required
                      invalid={!!errors.FollowUpReminderDays}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">in days</span>
                    </div>
                  </InputGroup>
                  {errors?.FollowUpReminderDays?.message ? (
                    <FormFeedback type="invalid" className="d-block">
                      {errors?.FollowUpReminderDays?.message}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            />
          </div>
        </Col>
        <Col sm="6">
          <div className="mb-3">
            <Label>Max Follow Up</Label>
            <Controller
              name="MaxFollowUp"
              control={control}
              render={({ field }) => (
                <>
                  <InputGroup>
                    <Input
                      {...field}
                      id="MaxFollowUp"
                      type="text"
                      required
                      invalid={!!errors.MaxFollowUp}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">times</span>
                    </div>
                  </InputGroup>
                  {errors?.MaxFollowUp?.message ? (
                    <FormFeedback type="invalid" className="d-block">
                      {errors?.MaxFollowUp?.message}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            />
          </div>
          <div className="mb-3">
            <Label>Recipient </Label>
            <Controller
              name="NotificationRecipients"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    id="NotificationRecipients"
                    options={notificationRecipients}
                    getOptionLabel={option => option.Recipient}
                    getOptionValue={option => option.RecipientID}
                    required
                    aria-invalid={!!errors.NotificationRecipients}
                    classNamePrefix="select2-selection"
                  />
                  {errors?.NotificationRecipients?.message ? (
                    <FormFeedback type="invalid" className="d-block">
                      {errors?.NotificationRecipients?.message}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            />
          </div>
          {NotificationRecipient.RecipientID === 2 ? (
            <div className="mb-3">
              <Controller
                name="SpecificRecipients"
                control={control}
                render={({ field }) => (
                  <>
                    <textarea
                      {...field}
                      className="form-control d-block"
                      id="SpecificRecipients"
                      required
                    />
                    {errors?.SpecificRecipients?.message ? (
                      <FormFeedback type="invalid" className="d-block">
                        {errors?.SpecificRecipients?.message}
                      </FormFeedback>
                    ) : null}
                  </>
                )}
              />
              <span className="text-muted">
                Recipients email addresses - separate with semi collon (;)
              </span>
            </div>
          ) : (
            ""
          )}
          <div className="mb-3">
            <Label>Active</Label>
            <div>
              <Controller
                name="IsActive"
                control={control}
                render={({ field }) => (
                  <>
                    <Switch
                      {...field}
                      id="IsActive"
                      checked={field.value}
                      uncheckedIcon={<Offsymbol />}
                      checkedIcon={<OnSymbol />}
                      className="me-1 mb-sm-8 mb-2"
                      onColor="#626ed4"
                    />
                  </>
                )}
              />
            </div>
          </div>
        </Col>
      </Row>{" "}
      <Row>
        <Col sm="12">
          <div className="mb-3">
            <Label>Template</Label>
            <div>
              <Editor
                editorState={editorState}
                onEditorStateChange={handleEditorChange}
                wrapperClassName="wrapper-class"
                editorClassName="editor-class"
                toolbarClassName="toolbar-class"
              />
              {errors?.Template?.message ? (
                <FormFeedback type="invalid" className="d-block">
                  {errors?.Template?.message}
                </FormFeedback>
              ) : null}
            </div>
          </div>
        </Col>
      </Row>
      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" className="btn" onClick={handleSave}>
          {routeParams.id === "new" ? "Save" : "Update"}
        </Button>
        <Button
          type="submit"
          color="secondary"
          onClick={onCancelClick}
          className=""
        >
          Cancel
        </Button>
      </div>
    </FormProvider>
  )
}
export default BasicInfoTab
